import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { fetchProduct, clearProduct } from '../../store/slices/productSlice';
import { createChatRoom } from '../../store/slices/chatSlice';
import {
  Container, Grid, Typography, Box, Button, Chip, Card, CardContent, CardMedia,
  Table, TableBody, TableRow, TableCell, TableContainer, Divider, CircularProgress,
  Tabs, Tab, Paper, List, ListItem, ListItemIcon, ListItemText
} from '@mui/material';
import {
  Chat, RequestQuote, LocalShipping, ArrowBack, Download,
  InsertDriveFile, AddShoppingCart
} from '@mui/icons-material';
import { addToCart } from '../../store/slices/cartSlice';
import { toast } from 'react-toastify';

const ProductDetailPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { product, loading } = useSelector(state => state.products);
  const { isAuthenticated } = useSelector(state => state.auth);
  const [selectedImage, setSelectedImage] = useState(0);
  const [tab, setTab] = useState(0);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    dispatch(fetchProduct(id));
    return () => dispatch(clearProduct());
  }, [dispatch, id]);

  const handleChat = async () => {
    if (!isAuthenticated) { navigate('/login'); return; }
    const result = await dispatch(createChatRoom({ productId: product._id, type: 'product_inquiry' }));
    if (result.payload) navigate('/customer/chat');
  };

  const handleRequestQuote = () => {
    if (!isAuthenticated) { navigate('/login'); return; }
    navigate(`/customer/quotes?request=${product._id}`);
  };

  const handleAddToCart = () => {
    if (!isAuthenticated) { navigate('/login'); return; }
    dispatch(addToCart({
      productId: product._id,
      name: product.name,
      nameAr: product.nameAr,
      osesgCode: product.osesgCode,
      image: product.images?.find(i => i.isPrimary)?.url || product.images?.[0]?.url,
      unitPrice: product.pricing?.basePrice || 0,
      quantity: qty,
      supplierId: product.supplier,
      moq: product.pricing?.moq || 1
    }));
    toast.success(`${product.name} added to cart!`);
    navigate('/customer/cart');
  };

  if (loading || !product) {
    return <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>;
  }

  const primaryImage = product.images?.find(img => img.isPrimary) || product.images?.[0];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} sx={{ mb: 2 }}>
        Back to Products
      </Button>

      <Grid container spacing={4}>
        {/* Images Gallery */}
        <Grid item xs={12} md={6}>
          <Box sx={{ bgcolor: '#f5f5f5', borderRadius: 3, p: 2, minHeight: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {product.images?.length > 0 ? (
              <Box
                role="img"
                aria-label={product.name}
                sx={{
                  width: '100%',
                  maxWidth: '100%',
                  height: 400,
                  backgroundImage: `url(${product.images[selectedImage]?.url || primaryImage?.url})`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'center',
                  backgroundSize: 'contain'
                }}
              />
            ) : (
              <Typography variant="h1" color="text.disabled">📦</Typography>
            )}
          </Box>
          {product.images?.length > 1 && (
            <Box sx={{ display: 'flex', gap: 1, mt: 2, overflowX: 'auto', pb: 1 }}>
              {product.images.map((img, i) => (
                <Box key={i} onClick={() => setSelectedImage(i)}
                  sx={{
                    width: 80, height: 80, bgcolor: '#f5f5f5', borderRadius: 1, flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                    border: selectedImage === i ? '2px solid #1a237e' : '2px solid transparent',
                    transition: 'border 0.2s'
                  }}>
                  <Box
                    role="img"
                    aria-label={img.alt || `${product.name} image ${i + 1}`}
                    sx={{
                      width: '100%',
                      height: '100%',
                      backgroundImage: `url(${img.url})`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'center',
                      backgroundSize: 'contain'
                    }}
                  />
                </Box>
              ))}
            </Box>
          )}
        </Grid>

        {/* Product Info */}
        <Grid item xs={12} md={6}>
          <Chip label={product.osesgCode} color="primary" sx={{ mb: 1 }} />
          <Typography variant="h4" fontWeight={700} gutterBottom>{product.name}</Typography>
          {product.nameAr && <Typography variant="h6" color="text.secondary" dir="rtl" gutterBottom>{product.nameAr}</Typography>}

          {product.shortDescription && (
            <Typography variant="body1" color="text.secondary" paragraph>{product.shortDescription}</Typography>
          )}

          {/* Price */}
          <Box sx={{ bgcolor: '#fff3e0', p: 2, borderRadius: 2, mb: 3 }}>
            {product.pricing?.priceType === 'negotiable' ? (
              <Typography variant="h5" color="secondary" fontWeight={700}>Price: Negotiable</Typography>
            ) : (
              <Typography variant="h5" color="secondary" fontWeight={700}>
                ${product.pricing?.basePrice?.toLocaleString()}
                {product.pricing?.priceType === 'range' && ` - $${product.pricing?.maxPrice?.toLocaleString()}`}
                <Typography component="span" variant="body2" color="text.secondary"> / unit</Typography>
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary">
              MOQ: {product.pricing?.moq || 1} units | Lead Time: {product.shipping?.leadTime} days
            </Typography>
          </Box>

          {/* Quantity Selector */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Typography variant="body2" color="text.secondary">Quantity:</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', border: '1px solid #ccc', borderRadius: 1, overflow: 'hidden' }}>
              <Box component="button" onClick={() => setQty(q => Math.max(product.pricing?.moq || 1, q - 1))}
                sx={{ px: 1.5, py: 0.5, border: 'none', bgcolor: '#f5f5f5', cursor: 'pointer', fontSize: 18 }}>−</Box>
              <Box sx={{ px: 2, py: 0.5, fontWeight: 700, minWidth: 36, textAlign: 'center' }}>{qty}</Box>
              <Box component="button" onClick={() => setQty(q => q + 1)}
                sx={{ px: 1.5, py: 0.5, border: 'none', bgcolor: '#f5f5f5', cursor: 'pointer', fontSize: 18 }}>+</Box>
            </Box>
            {product.pricing?.moq > 1 && (
              <Typography variant="caption" color="warning.main">Min: {product.pricing.moq} units</Typography>
            )}
          </Box>

          {/* Actions */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Button variant="contained" color="secondary" size="large" startIcon={<AddShoppingCart />}
              onClick={handleAddToCart} sx={{ flex: 1 }}>
              Add to Cart
            </Button>
            <Button variant="outlined" color="secondary" size="large" startIcon={<RequestQuote />}
              onClick={handleRequestQuote}>
              {t('request_quote')}
            </Button>
          </Box>
          <Box sx={{ mb: 3 }}>
            <Button variant="outlined" size="medium" startIcon={<Chat />}
              onClick={handleChat} fullWidth>
              {t('contact_support')}
            </Button>
          </Box>

          {/* Quick Info */}
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <LocalShipping color="primary" />
                <Typography variant="body2">Shipping & logistics managed by OSESG</Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">
                Including customs clearance, installation, and technical support
              </Typography>
            </CardContent>
          </Card>

          <Box display="flex" gap={1} flexWrap="wrap">
            {product.isCustomManufacturing && (
              <Chip label="✨ Custom Manufacturing Available" color="success" />
            )}
            {product.isVIPOnly && (
              <Chip label="👑 VIP Exclusive" color="warning" />
            )}
            {product.category?.name && (
              <Chip label={product.category.name} variant="outlined" />
            )}
          </Box>
        </Grid>
      </Grid>

      {/* Detail Tabs */}
      <Paper sx={{ mt: 4, mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable">
          <Tab label="Description" />
          {product.specifications?.length > 0 && <Tab label="Specifications" />}
          {product.videos?.length > 0 && <Tab label={`Videos (${product.videos.length})`} />}
          {product.documents?.length > 0 && <Tab label={`Documents (${product.documents.length})`} />}
          {(product.applications?.length > 0 || product.advantages?.length > 0) && <Tab label="Usage & Benefits" />}
        </Tabs>
      </Paper>

      {/* Tab: Description */}
      {tab === 0 && (
        <Box sx={{ py: 2 }}>
          <Typography variant="h5" fontWeight={600} gutterBottom>Description</Typography>
          <Typography variant="body1" paragraph sx={{ whiteSpace: 'pre-line' }}>{product.description}</Typography>
          {product.descriptionAr && (
            <Box sx={{ bgcolor: '#f5f5f5', p: 3, borderRadius: 2, mt: 2 }}>
              <Typography variant="h6" gutterBottom dir="rtl">الوصف</Typography>
              <Typography variant="body1" dir="rtl" sx={{ whiteSpace: 'pre-line' }}>{product.descriptionAr}</Typography>
            </Box>
          )}
        </Box>
      )}

      {/* Tab: Specifications */}
      {tab === 1 && product.specifications?.length > 0 && (
        <Box sx={{ py: 2 }}>
          <Typography variant="h5" fontWeight={600} gutterBottom>{t('specifications')}</Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableBody>
                {product.specifications.map((spec, i) => (
                  <TableRow key={i}>
                    <TableCell sx={{ fontWeight: 600, width: '40%', bgcolor: '#f5f5f5' }}>{spec.key}</TableCell>
                    <TableCell>{spec.value} {spec.unit}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Tab: Videos */}
      {((product.specifications?.length > 0 && tab === 2) || (!product.specifications?.length && tab === 1)) && product.videos?.length > 0 && (
        <Box sx={{ py: 2 }}>
          <Typography variant="h5" fontWeight={600} gutterBottom>Operational Videos / فيديوهات تشغيلية</Typography>
          <Grid container spacing={3}>
            {product.videos.map((vid, i) => (
              <Grid item xs={12} sm={6} key={i}>
                <Card>
                  <Box sx={{ bgcolor: '#000', overflow: 'hidden' }}>
                    <video width="100%" height="250" controls>
                      <source src={vid.url} />
                    </video>
                  </Box>
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight={600}>{vid.title || `Video ${i + 1}`}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Tab: Documents - compute dynamically */}
      {(() => {
        let docTabIndex = 1;
        if (product.specifications?.length > 0) docTabIndex++;
        if (product.videos?.length > 0) docTabIndex++;
        if (tab === docTabIndex && product.documents?.length > 0) {
          return (
            <Box sx={{ py: 2 }}>
              <Typography variant="h5" fontWeight={600} gutterBottom>Product Documents / مرفقات المنتج</Typography>
              <Paper variant="outlined">
                <List>
                  {product.documents.map((doc, i) => (
                    <ListItem key={i} divider>
                      <ListItemIcon><InsertDriveFile color="primary" /></ListItemIcon>
                      <ListItemText
                        primary={doc.name}
                        secondary={<Chip label={doc.type} size="small" variant="outlined" />}
                      />
                      <Button size="small" variant="outlined" href={doc.url} target="_blank" startIcon={<Download />}>
                        Download
                      </Button>
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Box>
          );
        }
        return null;
      })()}

      {/* Tab: Usage & Benefits - last tab */}
      {(() => {
        let usageTabIndex = 1;
        if (product.specifications?.length > 0) usageTabIndex++;
        if (product.videos?.length > 0) usageTabIndex++;
        if (product.documents?.length > 0) usageTabIndex++;
        if (tab === usageTabIndex && (product.applications?.length > 0 || product.advantages?.length > 0)) {
          return (
            <Box sx={{ py: 2 }}>
              {product.applications?.length > 0 && (
                <Box mb={3}>
                  <Typography variant="h5" fontWeight={600} gutterBottom>{t('applications')}</Typography>
                  <Box display="flex" gap={1} flexWrap="wrap">
                    {product.applications.map((app, i) => <Chip key={i} label={app} variant="outlined" />)}
                  </Box>
                </Box>
              )}
              {product.advantages?.length > 0 && (
                <Box mb={3}>
                  <Typography variant="h5" fontWeight={600} gutterBottom>{t('advantages')}</Typography>
                  <Grid container spacing={1}>
                    {product.advantages.map((adv, i) => (
                      <Grid item xs={12} sm={6} key={i}>
                        <Card variant="outlined" sx={{ p: 1.5 }}>
                          <Typography variant="body2">✅ {adv}</Typography>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
            </Box>
          );
        }
        return null;
      })()}
    </Container>
  );
};

export default ProductDetailPage;
